import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, Smartphone, Server, ArrowRight, Zap, Shield, Clock, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = [
  { id: "web", label: "Web Development" },
  { id: "mobile", label: "Mobile App" },
  { id: "desktop", label: "Desktop Software" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "api", label: "API/Backend" },
];

const sectors = ["Finance", "Healthcare", "Retail", "Education", "Real Estate", "Other"];
const budgetRanges = ["R5,000 - R15,000", "R15,000 - R50,000", "R50,000 - R150,000", "R150,000+"];
const contactMethods = ["Email", "Phone", "WhatsApp"];
const hearSources = ["Google Search", "Social Media", "Referral", "LinkedIn", "Other"];
const timelineOptions = ["2 Weeks", "1 Month", "2 Months", "3 Months", "4 Months", "5 Months", "6 Months+"];
const techStacks = ["React/Node.js", "Python/Django", "PHP/Laravel", "WordPress", "React Native", "Swift (iOS)", "Kotlin (Android)", "No Preference / Advise Me"];

export default function LandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_contact: "",
    company_name: "",
    project_name: "",
    sector: "",
    tech_stack: "",
    description: "",
    budget_range: "",
    deadline: "",
    how_heard: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      toast.error("Please select a project type");
      return;
    }
    if (!formData.deadline) {
      toast.error("Please select a timeline");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        project_type: projectTypes.find((t) => t.id === selectedType)?.label || selectedType,
        attachments: [],
      };

      await axios.post(`${API}/projects`, payload);
      toast.success("Project request submitted!");
      navigate("/success");
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("project-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Noise overlay */}
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#050505]/80 backdrop-blur-lg border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#FFD600]" />
              <span className="font-heading font-bold text-lg sm:text-xl text-white" data-testid="logo">METDIGITAL</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={scrollToForm}
                className="bg-[#FFD600] text-black font-bold uppercase tracking-wider hover:bg-[#E6C200] rounded-none px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm md:px-6 md:text-base"
                data-testid="nav-start-project-btn"
              >
                Start Project
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16" data-testid="hero-section">
        <div className="grid-lines" />
        <div className="hero-glow absolute inset-0" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[#FFD600] font-bold uppercase tracking-widest mb-4 animate-fade-in-up">
                South Africa's Premier Dev House
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
                Architects of the{" "}
                <span className="text-[#FFD600]">Digital Future</span>
              </h1>
              <p className="text-[#A1A1AA] text-lg mb-8 max-w-xl animate-fade-in-up animation-delay-200">
                We transform your vision into powerful digital solutions. From web applications to mobile platforms, 
                we build technology that drives your business forward.
              </p>
              <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
                <Button
                  onClick={scrollToForm}
                  className="btn-primary bg-[#FFD600] text-black font-bold uppercase tracking-wider hover:bg-[#E6C200] rounded-none px-8 py-6 text-lg"
                  data-testid="hero-start-project-btn"
                >
                  Start Your Project <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none px-8 py-6 text-lg border-white/20 text-white hover:border-[#FFD600] hover:text-[#FFD600] bg-transparent"
                  data-testid="hero-learn-more-btn"
                  onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#FFD600]/10 blur-3xl" />
                <img
                  src="https://images.unsplash.com/photo-1637073849667-91120a924221?q=80&w=2070&auto=format&fit=crop"
                  alt="Digital Innovation"
                  className="relative z-10 border border-[#27272A] w-full h-auto"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-8 w-8 text-[#FFD600]" />
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="text-[#3B82F6] font-bold uppercase tracking-widest mb-4">What We Build</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">
              End-to-End <span className="text-[#FFD600]">Development Services</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Monitor, title: "Web Applications", desc: "Custom web solutions built with modern frameworks and scalable architecture." },
              { icon: Smartphone, title: "Mobile Apps", desc: "Native and cross-platform mobile applications for iOS and Android." },
              { icon: Server, title: "API & Backend", desc: "Robust backend systems, APIs, and microservices infrastructure." },
            ].map((service, idx) => (
              <div
                key={idx}
                className="service-card bg-[#121212] border border-[#27272A] p-8 group"
                data-testid={`service-card-${idx}`}
              >
                <service.icon className="h-12 w-12 text-[#FFD600] mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-heading text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-[#A1A1AA]">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why MetDigital */}
      <section className="py-24 bg-[#050505]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#EF4444] font-bold uppercase tracking-widest mb-4">Why Choose Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-8">
                Built Different, <span className="text-[#FFD600]">Delivered Better</span>
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: Zap, title: "Rapid Development", desc: "Agile methodology ensuring quick turnaround without compromising quality." },
                  { icon: Shield, title: "Secure & Scalable", desc: "Enterprise-grade security with infrastructure that grows with you." },
                  { icon: Clock, title: "24/7 Support", desc: "Dedicated support team available around the clock for your needs." },
                ].map((feature, idx) => (
                  <div key={idx} className="flex gap-4" data-testid={`feature-${idx}`}>
                    <div className="flex-shrink-0 w-12 h-12 bg-[#121212] border border-[#27272A] flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-[#FFD600]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-white mb-1">{feature.title}</h3>
                      <p className="text-[#A1A1AA] text-sm">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#3B82F6]/10 blur-3xl" />
              <img
                src="https://images.unsplash.com/photo-1732354434112-a8ca835fe4d9?q=80&w=2070&auto=format&fit=crop"
                alt="Our Team"
                className="relative z-10 border border-[#27272A] w-full h-auto"
                data-testid="team-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Request Form */}
      <section id="project-form" className="py-24 bg-[#0A0A0A]" data-testid="form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FFD600] font-bold uppercase tracking-widest mb-4">Get Started</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Tell Us About Your <span className="text-[#3B82F6]">Project</span>
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Fill out the form below and we'll get back to you within 24 hours with a detailed proposal.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="project-request-form">
            {/* Project Info Section */}
            <div className="form-section">
              <h3 className="font-heading text-xl font-bold text-[#FFD600] mb-6">Project Information</h3>
              
              <div className="mb-6">
                <Label className="text-white mb-3 block">Project Type *</Label>
                <Select onValueChange={(v) => setSelectedType(v)} required>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-project-type">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    {projectTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id} className="text-white hover:bg-[#1E1E1E]" data-testid={`project-type-${type.id}`}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="project_name" className="text-white mb-2 block">Project Name *</Label>
                  <Input
                    id="project_name"
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Customer Portal"
                    className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-[#FFD600]"
                    data-testid="input-project-name"
                  />
                </div>
                <div>
                  <Label htmlFor="sector" className="text-white mb-2 block">Industry Sector *</Label>
                  <Select onValueChange={(v) => handleSelectChange("sector", v)} required>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-sector">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {sectors.map((s) => (
                        <SelectItem key={s} value={s} className="text-white hover:bg-[#1E1E1E]" data-testid={`sector-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="tech_stack" className="text-white mb-2 block">Preferred Tech Stack *</Label>
                  <Select onValueChange={(v) => handleSelectChange("tech_stack", v)} required>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-tech-stack">
                      <SelectValue placeholder="Select tech stack" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {techStacks.map((t) => (
                        <SelectItem key={t} value={t} className="text-white hover:bg-[#1E1E1E]" data-testid={`tech-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="description" className="text-white mb-2 block">Project Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Describe your project requirements, goals, and any specific features you need..."
                  className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 rounded-none min-h-[150px] focus:border-[#FFD600]"
                  data-testid="input-description"
                />
              </div>
            </div>

            {/* Company Info Section */}
            <div className="form-section">
              <h3 className="font-heading text-xl font-bold text-[#3B82F6] mb-6">Company & Contact</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name" className="text-white mb-2 block">Company Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your company name"
                    className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 rounded-none h-12"
                    data-testid="input-company-name"
                  />
                </div>
                <div>
                  <Label htmlFor="full_name" className="text-white mb-2 block">Your Full Name *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    placeholder="John Doe"
                    className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 rounded-none h-12"
                    data-testid="input-full-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="you@company.com"
                    className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 rounded-none h-12"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-white mb-2 block">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+27 XX XXX XXXX"
                    className="bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 rounded-none h-12"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="preferred_contact" className="text-white mb-2 block">Preferred Contact Method *</Label>
                  <Select onValueChange={(v) => handleSelectChange("preferred_contact", v)} required>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-contact-method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {contactMethods.map((m) => (
                        <SelectItem key={m} value={m} className="text-white hover:bg-[#1E1E1E]" data-testid={`contact-${m}`}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="how_heard" className="text-white mb-2 block">How did you hear about us? *</Label>
                  <Select onValueChange={(v) => handleSelectChange("how_heard", v)} required>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-how-heard">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {hearSources.map((s) => (
                        <SelectItem key={s} value={s} className="text-white hover:bg-[#1E1E1E]" data-testid={`source-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Budget & Timeline Section */}
            <div className="form-section">
              <h3 className="font-heading text-xl font-bold text-[#EF4444] mb-6">Budget & Timeline</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-white mb-2 block">Budget Range (ZAR) *</Label>
                  <Select onValueChange={(v) => handleSelectChange("budget_range", v)} required>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-budget">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {budgetRanges.map((b) => (
                        <SelectItem key={b} value={b} className="text-white hover:bg-[#1E1E1E]" data-testid={`budget-${b}`}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white mb-2 block">Project Timeline *</Label>
                  <Select onValueChange={(v) => handleSelectChange("deadline", v)} required>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none h-12" data-testid="select-timeline">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {timelineOptions.map((t) => (
                        <SelectItem key={t} value={t} className="text-white hover:bg-[#1E1E1E]" data-testid={`timeline-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary bg-[#FFD600] text-black font-bold uppercase tracking-wider hover:bg-[#E6C200] rounded-none py-6 text-lg disabled:opacity-50"
              data-testid="submit-btn"
            >
              {loading ? "Submitting..." : "Submit Project Request"}
              {!loading && <ArrowRight className="ml-2 h-5 w-5" />}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050505] border-t border-[#27272A] py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#FFD600]" />
              <span className="font-heading font-bold text-lg text-white">METDIGITAL</span>
            </div>
            <p className="text-[#A1A1AA] text-sm">
              © {new Date().getFullYear()} MetDigital. Architects of the Digital Future.
            </p>
            <div className="flex gap-6">
              <a href="mailto:info@metdigital.co.za" className="text-[#A1A1AA] hover:text-[#FFD600] transition-colors" data-testid="footer-email">
                info@metdigital.co.za
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
